/**
 * Het canvas: de pagina zoals hij wordt, met de bewerking eromheen.
 *
 * Het "papier" krijgt de breedte van de gekozen weergave (Desktop / Tablet /
 * Mobiel). Dat is een kijkhulp, geen apparaatsimulatie -- de echte website
 * doet zijn eigen responsive werk.
 */
import React from 'react';
import { ArrowDown, ArrowUp, Copy, LayoutTemplate, MoreHorizontal, Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';

import { gridTemplate, inferLayout, layoutDefinition } from '../layouts';
import { GRID_COLUMNS, layoutFromColumn } from '../grid';
import { availableWorkspaceWidth, CANVAS_ZOOM_STEPS, resolvedCanvasScale } from '../canvasZoom';
import type { CanvasZoom } from '../canvasZoom';
import type { DesktopViewportWidth } from '../canvasZoom';
import type { AlignmentGuide } from '../guides';
import { placeToolbar } from '../toolbarPlacement';
import { editorInteractionReducer, initialEditorInteraction } from '../interaction';
import type { EditorInteractionAction } from '../interaction';
import { CanvasFooter, CanvasHeader, canGoTransparent } from '../render/CanvasChrome';
import { resolveDecoration, resolveDivider, resolveSectionStyle, siteTokens } from '../render/tokens';
import { resolveSectionGeometry, sectionHeightFor, shouldCollapseEmptySection } from '../render/sectionGeometry';
import '../sites-elements.css';
import type { ColumnLayout, ContentBlock, MediaTarget, SiteChrome, SitePage, SiteSection, ViewportKey } from '../types';
import { CanvasBlock } from './CanvasBlock';

/**
 * Tablet collapse, mirroring `COLUMN_TEMPLATES_TABLET` in the Website Module.
 *
 * Chosen per layout rather than by a blanket rule: four equal columns want
 * 2x2 at this width, an asymmetric split wants to stack because its narrow
 * column becomes too thin to hold anything, and three equal columns survive
 * as three.
 */
function tabletTemplate(layout: ColumnLayout): string {
  const parts = layoutDefinition(layout).parts;
  if (parts.length <= 2) {
    const asymmetric = new Set(parts).size > 1;
    return asymmetric ? 'minmax(0, 1fr)' : gridTemplate(layout);
  }
  if (parts.length === 3 && new Set(parts).size === 1) return gridTemplate(layout);
  return 'repeat(2, minmax(0, 1fr))';
}


/** The site theme, or the renderer's own defaults when a draft predates the
 * design system. Matches `DEFAULT_THEME` in the Website Module's renderer. */
function themeOf(site: SiteChrome | null | undefined): SiteChrome['theme'] {
  return site?.theme ?? DEFAULT_CANVAS_THEME;
}

const DEFAULT_CANVAS_THEME: SiteChrome['theme'] = {
  colors: { primary: '#1d4ed8', secondary: '#0f172a', accent: '#f59e0b', background: '#ffffff', surface: '#f8fafc', text: '#0f172a', muted: '#64748b', dark: '#0b1220' },
  typography: { pairing: 'modern', headingScale: 'md', bodyScale: 'md' },
  buttons: { treatment: 'filled', radius: 'subtle', size: 'normal' },
  shapes: { radius: 'subtle', shadow: 'subtle', imageRadius: 'subtle' },
  layout: { contentWidth: 'normal', density: 'normal' },
  motion: 'normal',
};

/**
 * The width each preview actually simulates.
 *
 * Desktop is a real 1280px, not "whatever space is left". The canvas sits
 * between two panels, so on a 1600px screen it has roughly 775px -- below the
 * 860px breakpoint at which the header collapses. Rendering at the available
 * width therefore showed a collapsed, tablet-shaped header while the user had
 * Desktop selected. The paper is rendered at the true width and scaled down
 * to fit instead, so container queries see the width being simulated.
 */
const DESIGN_WIDTH: Record<ViewportKey, number> = {
  desktop: 1280,
  tablet: 834,
  mobile: 390,
};

interface CanvasProps {
  page: SitePage | null;
  viewport: ViewportKey;
  desktopViewportWidth?: DesktopViewportWidth;
  selectedBlockId: string | null;
  selectedSectionId: string | null;
  blockInspectorOpen?: boolean;
  onSelectBlock: (blockId: string | null) => void;
  onOpenBlockInspector: (blockId: string) => void;
  onSelectSection: (sectionId: string | null) => void;
  onOpenSectionInspector: (sectionId: string) => void;
  onUpdateSection?: (sectionId: string, patch: Partial<SiteSection>) => void;
  onChangeBlockText: (blockId: string, text: string) => void;
  onUpdateBlock: (blockId: string, patch: Partial<ContentBlock>) => void;
  onOpenSectionLibrary: (index?: number) => void;
  onOpenElementLibrary: (index?: number, sectionId?: string, columnId?: string) => void;
  onMoveBlock: (from: number, to: number) => void;
  onDuplicateBlock: (blockId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  onDuplicateSection: (sectionId: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onMoveSection: (sectionId: string, direction: 'up' | 'down') => void;
  onMoveBlockToColumn: (blockId: string, sectionId: string, columnId: string) => void;
  onPlacement: (blockId: string, patch: Partial<import('../types').GridPlacement>) => void;
  onOpenMedia: (target: MediaTarget) => void;
  onGuidesChange?: (guides: AlignmentGuide[]) => void;
  /** All pages, so internal button links resolve by id as they do on the site. */
  allPages: SitePage[];
  /** Which piece of global chrome is selected, if any. */
  selectedChrome: 'header' | 'footer' | null;
  selectedFooterElementId?: string | null;
  onSelectFooterElement: (elementId: string) => void;
  onUpdateFooterElement: (elementId: string, patch: Partial<import('../types').FooterElement>) => void;
  onMigrateFooterFreeform?: (placements: Record<string, import('../types').FreeformGeometry>, viewport: import('../types').ViewportKey) => void;
  onUpdateFooter: (patch: Partial<import('../types').SiteFooter>) => void;
  onUpdateSettings: (patch: Partial<import('../types').SiteChrome['settings']>) => void;
  onUpdateTheme?: (patch: Partial<import('../types').SiteChrome['theme']>) => void;
  onSelectHeader: () => void;
  onOpenHeaderInspector: () => void;
  onSelectFooter: () => void;
  onOpenFooterInspector: () => void;
  site?: SiteChrome | null;
  mediaUrl: (assetId: string) => string;
}

export function Canvas({
  page,
  viewport,
  desktopViewportWidth = 1280,
  selectedBlockId,
  selectedSectionId,
  blockInspectorOpen = false,
  onSelectBlock,
  onOpenBlockInspector,
  onChangeBlockText,
  onUpdateBlock,
  onSelectSection,
  onOpenSectionInspector,
  onUpdateSection,
  onOpenSectionLibrary,
  onOpenElementLibrary,
  onMoveBlock,
  onDuplicateBlock,
  onDeleteBlock,
  onDuplicateSection,
  onDeleteSection,
  onMoveSection,
  onMoveBlockToColumn,
  onPlacement,
  onOpenMedia,
  onGuidesChange,
  allPages,
  selectedChrome,
  selectedFooterElementId,
  onSelectFooterElement,
  onUpdateFooterElement,
  onMigrateFooterFreeform,
  onUpdateFooter,
  onUpdateSettings,
  onUpdateTheme,
  onSelectHeader,
  onOpenHeaderInspector,
  onSelectFooter,
  onOpenFooterInspector,
  site,
  mediaUrl,
}: CanvasProps) {
  const frameRef = React.useRef<HTMLDivElement | null>(null);
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const surfaceRef = React.useRef<HTMLDivElement | null>(null);
  const previousViewportRef = React.useRef<ViewportKey>(viewport);
  const [frameWidth, setFrameWidth] = React.useState(0);
  const [workspaceClientWidth, setWorkspaceClientWidth] = React.useState(0);
  const [paperHeight, setPaperHeight] = React.useState(0);
  const [paperBorderWidth, setPaperBorderWidth] = React.useState(0);
  const [showGrid, setShowGrid] = React.useState(false);
  const [activeGuides, setActiveGuides] = React.useState<AlignmentGuide[]>([]);
  const [sectionToolbarPosition, setSectionToolbarPosition] = React.useState<{ left: number; top: number } | null>(null);
  const sectionToolbarRef = React.useRef<HTMLDivElement | null>(null);
  const [interaction, dispatchInteraction] = React.useReducer(editorInteractionReducer, initialEditorInteraction);
  // Fit is the safe default for the Builder workspace. Manual zoom remains an
  // explicit inspection mode and may intentionally create horizontal scroll.
  const [zoom, setZoom] = React.useState<CanvasZoom>('fit');

  // Measure the actual editable workspace, not the viewport or the frame.
  // `clientWidth` excludes a vertical scrollbar and includes padding; removing
  // the computed inline padding leaves exactly the content box in which the
  // scaled frame is centered. This also reacts when the inspector opens or
  // closes and when the browser workspace is resized.
  React.useLayoutEffect(() => {
    const workspace = surfaceRef.current;
    if (!workspace) return;

    const measure = () => {
      const styles = getComputedStyle(workspace);
      setWorkspaceClientWidth((current) => current === workspace.clientWidth ? current : workspace.clientWidth);
      const nextWidth = availableWorkspaceWidth(workspace.clientWidth, Number.parseFloat(styles.paddingLeft || '0'), Number.parseFloat(styles.paddingRight || '0'));
      setFrameWidth((current) => current === nextWidth ? current : nextWidth);
    };

    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(workspace);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // The paper's own height drives the frame, so the scroll area matches what
  // is on screen as content grows and shrinks. Selection never writes back to
  // scrollTop/scrollLeft.
  React.useLayoutEffect(() => {
    const paper = paperRef.current;
    if (!paper) return;
    const measure = (entry?: ResizeObserverEntry) => {
      if (entry) {
        const nextHeight = entry.contentRect.height;
        setPaperHeight((current) => current === nextHeight ? current : nextHeight);
      }
      const nextBorderWidth = Math.max(0, paper.offsetWidth - paper.clientWidth);
      setPaperBorderWidth((current) => current === nextBorderWidth ? current : nextBorderWidth);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver((entries) => {
      const [entry] = entries;
      measure(entry);
    });
    measure();
    observer?.observe(paper);
    return () => observer?.disconnect();
  }, []);

  // A responsive preview is a different document geometry. Reusing the raw
  // scrollTop from Desktop after switching to Tablet/Mobile opens the new
  // viewport halfway down the hero (or even in a later section). Always start
  // a new preview at its top. Selection is deliberately not a reveal command:
  // a selected lower element must not hide the hero when the owner changes
  // device preview.
  React.useLayoutEffect(() => {
    if (previousViewportRef.current === viewport) return;
    previousViewportRef.current = viewport;

    const surface = surfaceRef.current;
    if (!surface) return;
    surface.scrollTop = 0;
    surface.scrollLeft = 0;

    let frame = 0;
    let settledFrames = 0;
    const settleViewport = () => {
      // ResizeObserver/layout settling can otherwise reintroduce the old
      // scroll anchor one frame after the device switch.
      surface.scrollTop = 0;
      surface.scrollLeft = 0;
      settledFrames += 1;
      if (settledFrames >= 4) return;
      frame = window.requestAnimationFrame(settleViewport);
    };
    frame = window.requestAnimationFrame(settleViewport);
    return () => window.cancelAnimationFrame(frame);
  }, [selectedBlockId, viewport]);

  const logicalWidth = viewport === 'desktop' ? desktopViewportWidth : DESIGN_WIDTH[viewport];
  const paperFootprintWidth = logicalWidth + paperBorderWidth;
  const scale = resolvedCanvasScale(zoom, frameWidth, paperFootprintWidth);

  React.useLayoutEffect(() => {
    const toolbar = sectionToolbarRef.current;
    const paper = paperRef.current;
    const section = selectedSectionId ? paper?.querySelector<HTMLElement>(`[data-zoea-section-id="${CSS.escape(selectedSectionId)}"]`) : null;
    if (!toolbar || !paper || !section || !selectedSectionId) {
      setSectionToolbarPosition(null);
      return;
    }
    const target = section.getBoundingClientRect();
    const bounds = paper.getBoundingClientRect();
    const toolbarRect = toolbar.getBoundingClientRect();
    const position = placeToolbar(target, toolbarRect.width, toolbarRect.height, bounds);
    setSectionToolbarPosition({ left: (position.left - target.left) / Math.max(scale, 0.01), top: (position.top - target.top) / Math.max(scale, 0.01) });
  }, [selectedSectionId, selectedBlockId, scale, paperHeight]);

  if (!page) {
    return (
      <div className="zoea-canvas-surface flex flex-1 items-center justify-center p-8">
        <div className="max-w-sm text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-400 shadow-sm">
            <LayoutTemplate className="h-6 w-6" />
          </span>
          <h2 className="mt-5 text-lg font-bold text-slate-900">Nog geen pagina&apos;s</h2>
          <p className="mt-2 text-sm text-slate-500">
            Maak links je eerste pagina aan. Die wordt automatisch de homepage van je website.
          </p>
        </div>
      </div>
    );
  }

  const sections = sectionsForPage(page);
  const terminalSection = sections[sections.length - 1];
  const terminalSectionIsCollapsedEmpty = terminalSection
    ? shouldCollapseEmptySection(
      terminalSection.columns.every((column) => column.blocks.length === 0),
      Boolean(terminalSection.style?.height),
      true,
      site?.footer.connectToLastSection === true,
    )
    : false;
  const selectedCanvasBlock = selectedBlockId ? sections.flatMap((item) => item.columns.flatMap((column) => column.blocks)).find((block) => block.blockId === selectedBlockId) : null;
  const responsiveOverrideActive = viewport !== 'desktop' && Boolean(selectedCanvasBlock?.layout?.[viewport]);
  const theme = themeOf(site);
  const pageWidth = theme.layout.pageWidth ?? 'inset';
  const pageScope = `page-${page.pageId.replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 80)}`;
  const pageDeveloperCss = scopePageDeveloperCss(page.developerCss ?? '', pageScope);

  // Scale the paper down to whatever space the canvas column has, never up:
  // a 390px mobile preview blown up to fill a wide screen would be a
  // magnifying glass, not a preview.
  // Internal links resolve by page id, exactly as on the published site, so a
  // button pointing at a deleted page previews as inert here too.
  const pathForPageId = (pageId: string): string | null => {
    const target = allPages.find((item) => item.pageId === pageId);
    return target ? (target.slug ? `/${target.slug}` : '/') : null;
  };

  const selectElement = (blockId: string | null) => {
    if (blockId) dispatchInteraction({ type: 'selectElement', targetId: blockId });
    else dispatchInteraction({ type: 'clear' });
    onSelectBlock(blockId);
  };
  const openElementInspector = (blockId: string) => {
    dispatchInteraction({ type: 'openInspector', targetId: blockId });
    onOpenBlockInspector(blockId);
  };
  const selectSection = (sectionId: string | null) => {
    if (sectionId) dispatchInteraction({ type: 'selectSection', sectionId });
    else dispatchInteraction({ type: 'clear' });
    onSelectSection(sectionId);
  };
  const handleInteraction = (action: EditorInteractionAction) => {
    dispatchInteraction(action);
  };
  const updateBlockPlacement = (blockId: string, patch: Partial<import('../types').GridPlacement>) => {
    onPlacement(blockId, patch);
  };
  const isManipulating = interaction.phase === 'DRAGGING_ELEMENT' || interaction.phase === 'RESIZING_ELEMENT';
  const paperRect = paperRef.current?.getBoundingClientRect();
  // Absolute children are positioned from the paper's padding box, while
  // getBoundingClientRect() starts at the transformed border box. Include the
  // scaled border once so a guide at a surface center is drawn on that exact
  // screen pixel instead of drifting by the paper border width.
  const paperContentOrigin = {
    left: paperRect ? paperRect.left + (paperRef.current?.clientLeft ?? 0) * scale : 0,
    top: paperRect ? paperRect.top + (paperRef.current?.clientTop ?? 0) * scale : 0,
  };

  const sectionStyleFor = (section: SiteSection): React.CSSProperties => {
    const resolved = resolveSectionStyle(theme, section.style);
    const geometry = resolveSectionGeometry(section, viewport);
    return {
      ...resolved.variables,
      background: resolved.background,
      color: 'var(--zs-color-text)',
      // Content owns the section's actual height. An authored section height
      // is a minimum/floor, never a clipping box; automatic sections remain
      // `height: auto` so cards, images and text can grow the band naturally.
      minHeight: section.style?.height ? `${geometry.height}px` : resolved.minHeight,
      textAlign: resolved.textAlign as React.CSSProperties['textAlign'],
      // `.zs-section` is a flex column (mirrored from the renderer), so
      // vertical alignment is a real setting rather than a no-op. Without
      // this the canvas ignored "Verticaal uitlijnen" entirely and a tall
      // hero pinned its content to the top while the site centred it.
      justifyContent: resolved.justifyContent,
      paddingBlock: resolved.paddingBlock,
      paddingTop: resolved.paddingTop,
      '--zs-section-padding': resolved.paddingBlock,
      '--zs-section-padding-top': resolved.paddingTop,
    } as React.CSSProperties;
  };

  const decorationFor = (section: SiteSection) => {
    const decoration = resolveDecoration(theme, section.style?.decoration);
    if (!decoration) return null;
    return (
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: decoration.inset,
          background: decoration.background,
          backgroundSize: decoration.backgroundSize,
          opacity: decoration.opacity,
          filter: decoration.filter,
          pointerEvents: 'none',
        }}
      />
    );
  };

  const dividerFor = (section: SiteSection, position: 'top' | 'bottom') => {
    const shape = resolveDivider(position === 'top' ? section.style?.topDivider : section.style?.bottomDivider);
    if (!shape) return null;
    return (
      <svg
        role="button"
        tabIndex={0}
        aria-label={`${position === 'top' ? 'Bovenrand' : 'Onderrand'} van sectie aanpassen`}
        focusable="true"
        viewBox={shape.viewBox}
        preserveAspectRatio="none"
        onClick={(event) => {
          event.stopPropagation();
          onOpenSectionInspector(section.sectionId);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          event.stopPropagation();
          onOpenSectionInspector(section.sectionId);
        }}
        style={{
          position: 'absolute', left: 0, right: 0, width: '100%', height: shape.height, [position]: 0,
          transform: `${position === 'top' ? 'scaleY(-1)' : ''} ${shape.flip ? 'scaleX(-1)' : ''}`.trim() || undefined,
          fill: shape.customColor ?? (shape.color ? `var(--zs-color-${shape.color})` : 'var(--zs-color-background)'),
          pointerEvents: 'auto', cursor: 'pointer',
        }}
      >
        <path d={shape.path} />
      </svg>
    );
  };

  return (
    <div
      ref={surfaceRef}
      className="zoea-canvas-surface min-h-0 min-w-0 flex-1 overflow-auto p-6 sm:p-10"
      data-testid="sites-canvas-workspace"
      data-canvas-client-width={workspaceClientWidth}
      data-canvas-logical-width={logicalWidth}
      data-canvas-scale={scale}
      data-canvas-available-width={frameWidth}
      data-canvas-rendered-paper-width={paperFootprintWidth * scale}
      onMouseDownCapture={(event) => {
        const eventElement = event.target instanceof Element ? event.target : null;
        const editorTarget = event.button === 0
          ? eventElement?.closest('[data-zoea-block-id], [data-zoea-section-id], .zs-header, .zs-footer, button, [role="button"], summary, [tabindex]')
          : null;
        if (editorTarget && !eventElement?.closest('[contenteditable="true"]')) {
          // Everything inside the paper is an editor control, not a native
          // navigation target. Preventing the default mousedown focus stops
          // Chrome from scrolling the canvas to reveal a clicked header,
          // section, footer or block. Inline text editing opts back in.
          event.preventDefault();
        }
      }}
      onMouseDown={(event) => {
        // Klikken naast een blok heft de selectie op -- zo voelt het canvas
        // als een vlak en niet als een formulier.
        if (event.target === event.currentTarget) selectElement(null);
      }}
    >
      <div className="zoea-canvas-toolbar sticky top-0 z-50 mx-auto mb-4 flex max-w-[1600px] items-center justify-between gap-4 rounded-xl border border-slate-200/70 bg-white/90 px-3 py-2 text-xs shadow-sm backdrop-blur" aria-label="Canvaswerkbalk">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Canvas</p>
          <p className="truncate font-semibold text-slate-700">{viewport === 'desktop' ? 'Desktop' : viewport === 'tablet' ? 'Tablet' : 'Mobiel'} <span className="font-normal text-slate-400">· {logicalWidth} px</span></p>
          {responsiveOverrideActive ? <span className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">Aangepast voor {viewport === 'mobile' ? 'mobiel' : 'tablet'}</span> : null}
        </div>
        <div className="flex shrink-0 items-center gap-1 font-semibold text-slate-700">
          <button type="button" onClick={() => setZoom((value) => value === 'fit' ? 80 : CANVAS_ZOOM_STEPS[Math.max(0, CANVAS_ZOOM_STEPS.indexOf(value as (typeof CANVAS_ZOOM_STEPS)[number]) - 1)]!)} className="zoea-canvas-toolbar-button text-base leading-none" aria-label="Uitzoomen">−</button>
          <select aria-label="Canvas zoomniveau" value={zoom} onChange={(event) => setZoom(event.target.value === 'fit' ? 'fit' : Number(event.target.value))} className="zoea-canvas-zoom-select" data-testid="sites-canvas-zoom">
            {CANVAS_ZOOM_STEPS.map((step) => <option key={step} value={step}>{step}%</option>)}
            <option value="fit">Passend</option>
          </select>
          <button type="button" onClick={() => setZoom((value) => value === 'fit' ? 100 : CANVAS_ZOOM_STEPS[Math.min(CANVAS_ZOOM_STEPS.length - 1, CANVAS_ZOOM_STEPS.indexOf(value as (typeof CANVAS_ZOOM_STEPS)[number]) + 1)]!)} className="zoea-canvas-toolbar-button text-base leading-none" aria-label="Inzoomen">+</button>
          <span className="mx-2 h-5 w-px bg-slate-200" />
          <button type="button" onClick={() => setZoom('fit')} className={cn('zoea-canvas-toolbar-button', zoom === 'fit' && 'bg-teal-50 text-teal-700')}>Passend</button>
          <button type="button" onClick={() => setShowGrid((value) => !value)} aria-pressed={showGrid} className={cn('zoea-canvas-toolbar-button', showGrid && 'bg-slate-100 text-slate-900')}>Raster</button>
          <details className="relative">
            <summary className="zoea-canvas-toolbar-button cursor-pointer list-none rounded-lg">Pagina: {pageWidth === 'nav' ? 'navbreedte' : 'ingesprongen'}</summary>
            <div className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-2 text-left shadow-xl">
              <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Uitlijning pagina</p>
              <button type="button" onClick={() => onUpdateTheme?.({ layout: { ...theme.layout, pageWidth: 'inset' } })} className={cn('mt-1 w-full rounded-lg px-2.5 py-2 text-left text-xs font-semibold', pageWidth === 'inset' ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50')}>Ingesprongen</button>
              <button type="button" onClick={() => onUpdateTheme?.({ layout: { ...theme.layout, pageWidth: 'nav' } })} className={cn('w-full rounded-lg px-2.5 py-2 text-left text-xs font-semibold', pageWidth === 'nav' ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50')}>Gelijk aan navigatie</button>
              <p className="px-2 pb-1 pt-2 text-[10px] leading-relaxed text-slate-500">De sectie-inhoud volgt dan dezelfde linker- en rechterrand als de navigatie.</p>
            </div>
          </details>
        </div>
        <span className="sr-only">Beschikbare zoom: {CANVAS_ZOOM_STEPS.join(', ')} procent</span>
      </div>
      {/* Het papier houdt een minimumhoogte aan, ook als er nog nauwelijks
          inhoud staat: een pagina die precies om zijn eerste zin heen sluit
          leest als een kaartje, niet als een webpagina. */}
      <div
        ref={frameRef}
        className="mx-auto"
        style={{
          // The frame reserves the SCALED footprint, so the surrounding
          // scroll area is the size the user sees rather than the unscaled
          // logical viewport width the paper is actually laid out at.
          width: paperFootprintWidth * scale,
          height: paperHeight ? paperHeight * scale : undefined,
          // A transformed paper keeps its logical (unscaled) layout box. In
          // fit mode that box must not become horizontal scrollable overflow;
          // manual zoom intentionally keeps the existing scroll behaviour.
          overflowX: zoom === 'fit' ? 'clip' : undefined,
        }}
      >
      <div
        ref={paperRef}
        className="zoea-canvas-paper zs-canvas min-h-[75vh] overflow-hidden rounded-xl border border-slate-200 shadow-lg shadow-slate-900/5"
        style={{
          width: logicalWidth,
          transform: scale === 1 ? undefined : `scale(${scale})`,
          transformOrigin: 'top left',
          // The whole theme, as the same custom properties the published site
          // sets on its root. Every nested element then styles itself from
          // them, exactly as it does on the site.
          ...(site ? siteTokens(site.theme) : {}),
          backgroundColor: site?.theme.colors.background ?? '#ffffff',
          color: site?.theme.colors.text ?? '#0f172a',
        } as React.CSSProperties}
        data-testid="sites-canvas-paper"
      >
        {activeGuides.map((guide, index) => (
          <span key={`${guide.orientation}-${index}`} aria-hidden="true" data-guide-orientation={guide.orientation} data-guide-kind={guide.kind} data-guide-active={guide.active ? 'true' : 'false'} className={cn('pointer-events-none absolute z-40 bg-fuchsia-500/80', guide.active ? 'bg-fuchsia-500/90' : 'bg-fuchsia-300/40', guide.orientation === 'vertical' ? 'bottom-0 top-0' : 'left-0 right-0')} style={guide.orientation === 'vertical' ? { left: (guide.position - paperContentOrigin.left) / scale, width: `${1 / Math.max(scale, 0.01)}px` } : { top: (guide.position - paperContentOrigin.top) / scale, height: `${1 / Math.max(scale, 0.01)}px` }}>
            {guide.distance != null ? <span className="absolute whitespace-nowrap rounded bg-fuchsia-600 px-1 py-0.5 text-[9px] font-semibold text-white" style={{ transform: `scale(${1 / Math.max(scale, 0.01)})`, transformOrigin: 'left top' }}>{Math.round(guide.distance)} px</span> : null}
          </span>
        ))}
          <div className={cn('overflow-hidden rounded-2xl', viewport === 'mobile' && 'text-sm')}>
            <CanvasHeader
            site={site ?? null}
            pages={allPages}
            currentPageId={page.pageId}
            mobile={viewport === 'mobile'}
            allowTransparent={canGoTransparent(sections[0]?.style)}
            selected={selectedChrome === 'header'}
            onSelect={onSelectHeader}
            onOpenInspector={onOpenHeaderInspector}
            onOpenMedia={onOpenMedia}
            mediaUrl={mediaUrl}
          />
          <main data-zoea-page-scope={pageScope} data-zs-page-width={pageWidth} className="zs-page-scope">
          {pageDeveloperCss ? <style data-zoea-page-developer-css dangerouslySetInnerHTML={{ __html: pageDeveloperCss }} /> : null}
          {(() => {
            const firstTopSpacing = sections[0]?.style?.topSpacing ?? 'normal';
            return <div
              className={cn(
                pageWidth === 'nav' ? 'px-0' : 'px-8 sm:px-14',
                viewport === 'mobile' && (pageWidth === 'nav' ? 'px-0' : 'px-6'),
              )}
            >
              <div className="space-y-0">
                {sections.length > 0 ? <div className={cn(
                  'zoea-section-insertion group/section relative z-10 flex h-0 min-h-0 items-center justify-center p-0',
                )}><span className="absolute inset-x-10 border-t border-dashed border-slate-300" /><button type="button" onClick={() => onOpenSectionLibrary(0)} className={cn(
                  'absolute left-1/2 top-0 z-10 inline-flex -translate-x-1/2 items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-teal-400 hover:bg-teal-50 hover:text-teal-700',
                  firstTopSpacing === 'flush' && '-translate-y-1/2 px-3 py-1.5 text-xs',
                )}><Plus className="h-4 w-4" /> Sectie bovenaan toevoegen</button></div> : null}
                {sections.map((section, sectionIndex) => (
                  <React.Fragment key={section.sectionId}>
                    {(() => {
                      // The Builder always uses one 12/8/4-track interaction
                      // surface. Legacy column definitions are projected into
                      // the same placement model below; keeping a separate
                      // nested flow grid here was the source of elements
                      // changing rows when a neighbour was resized.
                      const canonicalGrid = true;
                      const sectionIsEmpty = section.columns.every((column) => column.blocks.length === 0);
                      // A last empty section is only an editor insertion
                      // affordance. When the owner asks the footer to join the
                      // content, an old authored section height must not keep
                      // an invisible band between the last real section and
                      // the footer.
                      const collapseEmptySection = shouldCollapseEmptySection(
                        sectionIsEmpty,
                        Boolean(section.style?.height),
                        sectionIndex === sections.length - 1,
                        site?.footer.connectToLastSection === true,
                      );
                      return (
                    <section
                      data-zoea-section-id={section.sectionId}
                      onClick={(event) => {
                        const target = event.target instanceof Element ? event.target : null;
                        if (target?.closest('[data-zoea-block-id], [data-testid^="sites-block-"]')) return;
                        selectSection(section.sectionId);
                      }}
                      className={cn(
                        'zs-section group/section relative transition',
                        `zs-section-${section.style?.colorScheme ?? 'light'}`,
                        collapseEmptySection && 'zoea-empty-section',
                        // Selection is a ring drawn INSIDE the section, so it
                        // never adds width or shifts the layout the owner is
                        // judging. At rest there is no border at all.
                        selectedSectionId === section.sectionId && 'ring-1 ring-inset ring-teal-400/70',
                      )}
                      style={{
                        ...sectionStyleFor(section),
                        // `space-y-6` is useful between sections, but it must
                        // not reintroduce a hidden gap for a flush first
                        // section. The section's own top padding is already
                        // zero in that mode.
                        ...(sectionIndex === 0 && firstTopSpacing === 'flush' ? { marginTop: 0 } : {}),
                        ...(collapseEmptySection ? {
                          minHeight: 0,
                          paddingBlock: 0,
                          paddingTop: 0,
                          '--zs-section-padding': '0px',
                          '--zs-section-padding-top': '0px',
                        } : {}),
                      }}
                    >
                        {(showGrid || isManipulating) && (
                        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-25" style={{ backgroundImage: 'repeating-linear-gradient(to right, rgba(13, 148, 136, 0.75) 0, rgba(13, 148, 136, 0.75) 1px, transparent 1px, transparent 16px), repeating-linear-gradient(to bottom, rgba(13, 148, 136, 0.75) 0, rgba(13, 148, 136, 0.75) 1px, transparent 1px, transparent 16px)', backgroundSize: '16px 16px' }} />
                      )}
                      {selectedSectionId === section.sectionId && (
                        <div ref={sectionToolbarRef} className="zoea-section-context-toolbar flex w-fit items-center gap-1 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg" style={{ ...(sectionToolbarPosition ? { left: sectionToolbarPosition.left, top: sectionToolbarPosition.top, right: 'auto' } : {}), transform: `scale(${1 / Math.max(scale, 0.01)})`, transformOrigin: 'top left' }} role="toolbar" aria-label="Sectie-acties">
                          <span className="px-2 text-xs font-bold text-slate-700">Sectie</span>
                          <button type="button" title="Element toevoegen" onClick={(event) => { event.stopPropagation(); onOpenElementLibrary(undefined, section.sectionId, section.columns[0]?.columnId); }} className="rounded-lg bg-teal-600 px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-teal-700">+ Toevoegen</button>
                          <button type="button" title="Sectie-instellingen" onClick={(event) => { event.stopPropagation(); onOpenSectionInspector(section.sectionId); }} className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100">Ontwerp</button>
                          <details className="relative">
                            <summary className="cursor-pointer list-none rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-100">Positie</summary>
                            <div className="absolute right-0 top-full z-50 mt-1 w-52 rounded-xl border border-slate-200 bg-white p-2 text-left shadow-xl">
                              <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Bovenruimte</p>
                              {(['flush', 'compact', 'normal'] as const).map((value) => (
                                <button key={value} type="button" onClick={(event) => { event.stopPropagation(); onUpdateSection?.(section.sectionId, { style: { ...section.style, topSpacing: value } }); }} className={cn('w-full rounded-lg px-2.5 py-2 text-left text-xs font-semibold', (section.style?.topSpacing ?? 'normal') === value ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50')}>
                                  {value === 'flush' ? 'Aansluitend' : value === 'compact' ? 'Kleine ruimte' : 'Normale ruimte'}
                                </button>
                              ))}
                              <p className="mt-1 border-t border-slate-100 px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Inhoud</p>
                              {(['top', 'center', 'bottom'] as const).map((value) => (
                                <button key={value} type="button" onClick={(event) => { event.stopPropagation(); onUpdateSection?.(section.sectionId, { style: { ...section.style, verticalAlign: value } }); }} className={cn('w-full rounded-lg px-2.5 py-2 text-left text-xs font-semibold', (section.style?.verticalAlign ?? 'center') === value ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-50')}>
                                  {value === 'top' ? 'Boven' : value === 'center' ? 'Midden' : 'Onder'}
                                </button>
                              ))}
                            </div>
                          </details>
                          <button type="button" title="Sectie omhoog" aria-label="Sectie omhoog" disabled={sectionIndex === 0} onClick={(event) => { event.stopPropagation(); onMoveSection(section.sectionId, 'up'); }} className="rounded-lg px-2 py-1.5 text-xs font-bold text-slate-500 transition enabled:hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
                          <button type="button" title="Sectie omlaag" aria-label="Sectie omlaag" disabled={sectionIndex === sections.length - 1} onClick={(event) => { event.stopPropagation(); onMoveSection(section.sectionId, 'down'); }} className="rounded-lg px-2 py-1.5 text-xs font-bold text-slate-500 transition enabled:hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
                          <span className="mx-0.5 h-5 w-px bg-slate-200" />
                          <details className="relative">
                            <summary className="flex cursor-pointer list-none rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800" aria-label="Meer sectieacties" title="Meer acties"><MoreHorizontal className="h-3.5 w-3.5" /></summary>
                            <div className="absolute right-0 top-full z-50 mt-1 grid min-w-44 gap-0.5 rounded-xl border border-slate-200 bg-white p-1.5 text-left shadow-xl">
                              <button type="button" disabled={sectionIndex === 0} onClick={(event) => { event.stopPropagation(); onMoveSection(section.sectionId, 'up'); }} className="rounded-lg px-3 py-2 text-xs font-semibold text-slate-600 transition enabled:hover:bg-slate-50 disabled:opacity-40"><ArrowUp className="mr-2 inline h-3.5 w-3.5" />Omhoog</button>
                              <button type="button" disabled={sectionIndex === sections.length - 1} onClick={(event) => { event.stopPropagation(); onMoveSection(section.sectionId, 'down'); }} className="rounded-lg px-3 py-2 text-xs font-semibold text-slate-600 transition enabled:hover:bg-slate-50 disabled:opacity-40"><ArrowDown className="mr-2 inline h-3.5 w-3.5" />Omlaag</button>
                              <button type="button" onClick={(event) => { event.stopPropagation(); onDuplicateSection(section.sectionId); }} className="rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-600 transition hover:bg-slate-50"><Copy className="mr-2 inline h-3.5 w-3.5" />Dupliceren</button>
                              <button type="button" onClick={(event) => { event.stopPropagation(); onDeleteSection(section.sectionId); }} className="rounded-lg px-3 py-2 text-left text-xs font-semibold text-red-600 transition hover:bg-red-50"><Trash2 className="mr-2 inline h-3.5 w-3.5" />Verwijderen</button>
                            </div>
                          </details>
                        </div>
                      )}
                      {/* The canvas grid is computed from the SAME layout table
                          the Website Module renders from, so a 1/4 + 3/4 split
                          looks here exactly as it will when published. Mobile
                          and tablet collapse to match the renderer's own
                          breakpoints. */}
                      {decorationFor(section)}
                      {dividerFor(section, 'top')}
                      {dividerFor(section, 'bottom')}
                      <div
                        className={cn('zs-section-inner grid items-start gap-5', canonicalGrid && 'grid-cols-12')}
                        style={{ maxWidth: resolveSectionStyle(theme, section.style).maxWidth, gridTemplateColumns: canonicalGrid ? `repeat(${GRID_COLUMNS[viewport]}, minmax(0, 1fr))` : viewport === 'mobile' ? 'minmax(0, 1fr)' : viewport === 'tablet' ? tabletTemplate(section.columnLayout ?? inferLayout(section.columns.length)) : gridTemplate(section.columnLayout ?? inferLayout(section.columns.length)) }}
                      >
                        {collapseEmptySection ? (
                          <div className="zoea-empty-section-add col-span-12 flex min-h-0 justify-center py-1">
                            <button
                              type="button"
                              aria-label="Element toevoegen aan lege sectie"
                              onClick={(event) => { event.stopPropagation(); onOpenElementLibrary(undefined, section.sectionId, section.columns[0]?.columnId); }}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-white/70 px-3 py-1.5 text-xs font-semibold text-slate-500 transition hover:border-teal-400 hover:bg-teal-50 hover:text-teal-700"
                            >
                              <Plus className="h-3.5 w-3.5" /> Element toevoegen
                            </button>
                          </div>
                        ) : section.columns.map((column, columnIndex) => (
                          <div key={column.columnId} className={cn('group/column min-h-24 rounded-xl px-2 py-2 transition hover:bg-white hover:ring-1 hover:ring-slate-200', canonicalGrid ? 'contents' : 'grid grid-cols-12 gap-x-2 gap-y-6')}>
                            <div className={canonicalGrid ? 'contents' : 'contents'}>
                              {column.blocks.length === 0 ? (() => {
                                const emptyPlacement = layoutFromColumn(section.columnLayout ?? inferLayout(section.columns.length), columnIndex)[viewport] ?? layoutFromColumn(section.columnLayout ?? inferLayout(section.columns.length), columnIndex).desktop;
                                return <button
                                  type="button"
                                  aria-label={`Element toevoegen aan kolom ${columnIndex + 1}`}
                                  onClick={(event) => { event.stopPropagation(); onOpenElementLibrary(undefined, section.sectionId, column.columnId); }}
                                  className={cn(
                                    'group/empty-column min-h-12 w-full rounded-xl border-2 border-dashed border-slate-300 bg-white/60 px-4 py-2 text-center text-sm font-semibold text-slate-500 transition hover:border-teal-400 hover:bg-teal-50 hover:text-teal-700',
                                    canonicalGrid ? 'col-start-auto' : 'col-span-12',
                                  )}
                                  style={canonicalGrid ? { gridColumn: `${emptyPlacement.gridStart} / span ${emptyPlacement.gridSpan}` } : undefined}
                                >
                                  <Plus className="mx-auto mb-2 h-4 w-4" />
                                  Element toevoegen
                                </button>;
                              })() : null}
                              {blocksForViewport(column.blocks, viewport).map((block) => {
                                const flatBlocks = sections.flatMap((item) => item.columns.flatMap((itemColumn) => itemColumn.blocks));
                                const index = flatBlocks.findIndex((item) => item.blockId === block.blockId);
                                const visualBlock = block.layout
                                  ? block
                                  : { ...block, layout: layoutFromColumn(section.columnLayout ?? inferLayout(section.columns.length), columnIndex) };
                                // Only the OTHER columns of this same section are
                                // offered: moving an element across sections is a
                                // different intent and would silently change which
                                // background and spacing it inherits.
                                const targets = section.columns
                                  .map((candidate, candidateIndex) => ({ candidate, candidateIndex }))
                                  .filter(({ candidate }) => candidate.columnId !== column.columnId)
                                  .map(({ candidate, candidateIndex }) => ({ columnId: candidate.columnId, label: `Naar kolom ${candidateIndex + 1}` }));
                                return <CanvasBlock key={block.blockId} block={visualBlock} index={index} total={flatBlocks.length} selected={block.blockId === selectedBlockId} inspectorOpen={blockInspectorOpen} interaction={interaction} onInteraction={handleInteraction} onSelect={() => selectElement(block.blockId)} onOpenInspector={() => openElementInspector(block.blockId)} onOpenMedia={() => onOpenMedia({ kind: 'block', blockId: block.blockId })} onChangeText={(text) => onChangeBlockText(block.blockId, text)} onUpdateBlock={(patch) => onUpdateBlock(block.blockId, patch)} onMove={onMoveBlock} onDuplicate={() => onDuplicateBlock(block.blockId)} onDelete={() => onDeleteBlock(block.blockId)} theme={themeOf(site)} pathForPageId={pathForPageId} mediaUrl={mediaUrl} columnTargets={targets} onMoveToColumn={(columnId) => onMoveBlockToColumn(block.blockId, section.sectionId, columnId)} viewport={viewport} canvasScale={scale} onPlacement={(patch) => updateBlockPlacement(block.blockId, patch)} onGuidesChange={(guides) => { setActiveGuides(guides); onGuidesChange?.(guides); }} />;
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                      {selectedSectionId === section.sectionId && onUpdateSection ? <SectionResizeHandle section={section} viewport={viewport} canvasScale={scale} onResize={(height) => onUpdateSection(section.sectionId, { style: { ...section.style, height } })} /> : null}
                    </section>
                      );
                    })()}
                    {sectionIndex < sections.length - 1 && !(terminalSectionIsCollapsedEmpty && sectionIndex === sections.length - 2) ? <div className="zoea-section-insertion group/section relative z-10 flex h-0 min-h-0 items-center justify-center p-0"><span className="absolute inset-x-10 border-t border-dashed border-slate-300" /><button type="button" onClick={() => onOpenSectionLibrary(sectionIndex + 1)} className="absolute left-1/2 top-0 z-10 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-teal-400 hover:bg-teal-50 hover:text-teal-700"><Plus className="h-4 w-4" /> Sectie toevoegen</button></div> : null}
                  </React.Fragment>
                ))}
              </div>
            </div>;
          })()}

          {sections.every((section) => section.columns.every((column) => column.blocks.length === 0)) ? (
            <div className="rounded-2xl border border-dashed border-slate-300 px-6 py-14 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700"><LayoutTemplate className="h-5 w-5" /></span>
              <h3 className="mt-4 text-base font-bold text-slate-900">Begin met een sectie</h3>
              <p className="mx-auto mt-1.5 max-w-xs text-sm text-slate-500">Kies een visuele opbouw. Je kunt elk onderdeel daarna direct aanpassen.</p>
              <button type="button" onClick={() => onOpenSectionLibrary(0)} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-teal-700"><Plus className="h-4 w-4" /> Sectie toevoegen</button>
            </div>
          ) : (
            <div className="relative z-10 flex h-0 justify-center">
              {!terminalSectionIsCollapsedEmpty ? <button type="button" onClick={() => onOpenSectionLibrary(sections.flatMap((section) => section.columns.flatMap((column) => column.blocks)).length)} className="absolute left-1/2 top-0 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-600 transition hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"><Plus className="h-4 w-4" /> Sectie toevoegen</button> : null}
            </div>
          )}
          <CanvasFooter
            site={site ?? null}
            pages={allPages}
            mobile={viewport === 'mobile'}
            viewport={viewport}
            selected={selectedChrome === 'footer'}
            selectedElementId={selectedFooterElementId}
            onSelect={onSelectFooter}
            onOpenInspector={onOpenFooterInspector}
            onSelectElement={onSelectFooterElement}
            onUpdateElement={onUpdateFooterElement}
            onMigrateFreeform={onMigrateFooterFreeform}
            onUpdateFooter={onUpdateFooter}
            onUpdateSettings={onUpdateSettings}
            onOpenMedia={onOpenMedia}
            mediaUrl={mediaUrl}
            canvasScale={scale}
            onGuidesChange={(guides) => { setActiveGuides(guides); onGuidesChange?.(guides); }}
          />
          </main>
          </div>
        </div>
      </div>
      </div>
  );
}

function sectionsForPage(page: SitePage): SiteSection[] {
  return page.sections?.length ? page.sections : [{ sectionId: 'legacy-section', layout: 'content', columns: [{ columnId: 'legacy-column', blocks: page.blocks }], spacing: 'md' }];
}

/**
 * A freeform Desktop drag can move a block above the source-order flow. The
 * narrow renderer stacks that same flow, so leaving the source array untouched
 * makes Desktop and Mobile disagree about the reading order. Only a clearly
 * upward-moved foreground/normal block is promoted; ordinary blocks keep
 * their authored source order and background decorations never participate.
 */
function blocksForViewport(blocks: ContentBlock[], viewport: ViewportKey): ContentBlock[] {
  if (viewport === 'desktop') return blocks;
  return blocks
    .map((block, index) => ({
      block,
      index,
      lead: block.type === 'badge',
      movedUp: (block.layout?.desktop?.offsetY ?? 0) < -24 && block.layout?.desktop?.layer !== 'background',
    }))
    .sort((left, right) => Number(right.lead) - Number(left.lead) || Number(right.movedUp) - Number(left.movedUp) || left.index - right.index)
    .map(({ block }) => block);
}

function SectionResizeHandle({ section, viewport, canvasScale, onResize }: { section: SiteSection; viewport: ViewportKey; canvasScale: number; onResize: (height: NonNullable<SiteSection['style']>['height']) => void }) {
  const startRef = React.useRef<{ y: number; height: number; node: HTMLElement } | null>(null);
  const onPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    event.preventDefault(); event.stopPropagation();
    const node = event.currentTarget.parentElement;
    if (!node) return;
    startRef.current = { y: event.clientY, height: sectionHeightFor(section.style?.height, viewport, section.style), node };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    const start = startRef.current;
    if (!start) return;
    const height = Math.max(160, Math.min(2000, Math.round(start.height + (event.clientY - start.y) / Math.max(canvasScale, 0.01))));
    start.node.style.height = `${height}px`;
    start.node.dataset.previewSectionHeight = String(height);
  };
  const onPointerUp = (event: React.PointerEvent<HTMLButtonElement>) => {
    const start = startRef.current;
    if (!start) return;
    const height = Number(start.node.dataset.previewSectionHeight ?? start.height);
    const next = { ...(section.style?.height ?? { desktop: start.height }) };
    if (viewport === 'desktop') next.desktop = height;
    if (viewport === 'tablet') next.tablet = height;
    if (viewport === 'mobile') next.mobile = height;
    delete start.node.dataset.previewSectionHeight;
    startRef.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
    onResize(next);
  };
  return <button type="button" aria-label="Sectiehoogte aanpassen" title="Sectiehoogte aanpassen" className="zoea-section-resize-handle" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} />;
}

function scopePageDeveloperCss(source: string, scope: string): string {
  if (!source.trim()) return '';
  const classSelector = /\.([A-Za-z][A-Za-z0-9_-]{0,63})\b/g;
  return source.split('}').filter(Boolean).map((rule) => {
    const open = rule.indexOf('{');
    if (open < 0) return '';
    const selectors = rule.slice(0, open).split(',').map((selector) => `[data-zoea-page-scope="${scope}"] ${selector.trim().replace(classSelector, '[class~="$1"]')}`).join(', ');
    return `${selectors} {${rule.slice(open + 1)}}`;
  }).filter(Boolean).join('\n');
}
